import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { FileDown, FileSpreadsheet, Activity } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import api from "../../lib/api";
import { money, monthLabel } from "../../lib/format";

const WALLET_NAMES = { bills: "Bills", household: "Household", savings: "Savings", emergency: "Emergency", unplanned: "Unplanned" };
const COLORS = { bills: "#EA580C", household: "#65A30D", savings: "#0D9488", emergency: "#0284C7", unplanned: "#D97706" };

export default function Reports({ open, onOpenChange, month, currency }) {
  const [report, setReport] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/reports?month=${month}`);
    setReport(data);
  }, [month]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!report) return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent><DialogHeader><DialogTitle>Report</DialogTitle><DialogDescription>Generating your monthly report…</DialogDescription></DialogHeader><p className="py-8 text-center text-slate-400">Loading…</p></DialogContent></Dialog>
  );

  const barData = report.wallets.map((w) => ({ name: WALLET_NAMES[w.key], Planned: w.planned, Actual: w.actual }));
  const pieData = report.wallets.map((w) => ({ name: WALLET_NAMES[w.key], value: w.actual })).filter((d) => d.value > 0);
  const scoreColor = report.health_score >= 75 ? "#10B981" : report.health_score >= 50 ? "#F59E0B" : "#EF4444";

  const exportCSV = () => {
    const rows = [["Wallet", "Planned", "Actual", "Variance"]];
    report.wallets.forEach((w) => rows.push([WALLET_NAMES[w.key], w.planned, w.actual, w.variance]));
    rows.push(["My Wallet", report.my_wallet.planned, report.my_wallet.actual, report.my_wallet.variance]);
    rows.push(["Income", report.income.target, report.income.actual, report.income.actual - report.income.target]);
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `epitaka-${month}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  const exportPDF = () => { window.print(); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center justify-between pr-6">
            <span>Monthly Report — {monthLabel(month)}</span>
          </DialogTitle>
          <DialogDescription>Planned vs actual summary, financial health score and exportable data.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 no-print">
          <Button size="sm" variant="outline" onClick={exportCSV} data-testid="export-csv-button"><FileSpreadsheet className="w-4 h-4 mr-1.5" /> Export Excel/CSV</Button>
          <Button size="sm" variant="outline" onClick={exportPDF} data-testid="export-pdf-button"><FileDown className="w-4 h-4 mr-1.5" /> Export PDF</Button>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <div className="p-4 rounded-xl border border-slate-200 flex items-center gap-3">
            <div className="relative w-14 h-14">
              <svg viewBox="0 0 36 36" className="w-14 h-14">
                <path d="M18 2.5a15.5 15.5 0 110 31 15.5 15.5 0 010-31" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                <path d="M18 2.5a15.5 15.5 0 110 31 15.5 15.5 0 010-31" fill="none" stroke={scoreColor} strokeWidth="3" strokeDasharray={`${report.health_score}, 100`} strokeLinecap="round" />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-sm font-bold font-mono-nums" style={{ color: scoreColor }}>{report.health_score}</span>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider flex items-center gap-1"><Activity className="w-3 h-3" /> Health Score</p>
              <p className="text-sm font-semibold text-slate-700">{report.health_score >= 75 ? "Excellent" : report.health_score >= 50 ? "Fair" : "Needs attention"}</p>
            </div>
          </div>
          <div className="p-4 rounded-xl border border-slate-200">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Income</p>
            <p className="font-mono-nums text-lg font-bold text-slate-800">{money(report.income.actual, currency)}</p>
            <p className="text-xs text-slate-400">of {money(report.income.target, currency)} target</p>
          </div>
          <div className="p-4 rounded-xl border border-slate-200">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Free Money</p>
            <p className={`font-mono-nums text-lg font-bold ${report.my_wallet.actual < 0 ? "text-red-600" : "text-emerald-700"}`}>{money(report.my_wallet.actual, currency)}</p>
            <p className="text-xs text-slate-400">incl. {money(report.my_wallet.carryover, currency)} carryover</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl border border-slate-200">
            <p className="text-sm font-semibold text-slate-700 mb-3">Planned vs Actual</p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => money(v, currency)} />
                <Bar dataKey="Planned" fill="#cbd5e1" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Actual" fill="#EA580C" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="p-4 rounded-xl border border-slate-200">
            <p className="text-sm font-semibold text-slate-700 mb-3">Actual Distribution</p>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} dataKey="value" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {pieData.map((d) => <Cell key={d.name} fill={COLORS[Object.keys(WALLET_NAMES).find((k) => WALLET_NAMES[k] === d.name)]} />)}
                </Pie>
                <Tooltip formatter={(v) => money(v, currency)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
